import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { auth } from '@/boot/firebase'

export async function standaloneBillingGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  console.log('Standalone billing guard - checking route:', to.name)
  const authStore = useAuthStore()
  
  // Check if route requires standalone auth
  if (to.meta.requiresStandaloneAuth) {
    console.log('Route requires standalone auth')
    
    // Wait a moment for Firebase auth to initialize if needed
    if (!auth.getCurrentUser() && localStorage.getItem('isAuthenticated') === 'true') {
      console.log('Waiting for Firebase auth to restore...')
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, 2000) // Max wait 2 seconds
        const unsubscribe = auth.onAuthStateChanged((user) => {
          clearTimeout(timeout)
          unsubscribe()
          resolve()
        })
      })
    }
    
    // Check multiple indicators of authentication state
    const hasAuthToken = localStorage.getItem('authToken')
    const isMarkedAuthenticated = localStorage.getItem('isAuthenticated') === 'true'
    const hasFirebaseUser = auth.getCurrentUser() !== null
    const storeAuthenticated = authStore.isAuthenticated
    
    // User is considered authenticated if any of these are true
    const isAuthenticated = storeAuthenticated || hasFirebaseUser || (hasAuthToken && isMarkedAuthenticated)
    
    console.log('Auth check results:', {
      storeAuthenticated,
      hasFirebaseUser,
      hasAuthToken: !!hasAuthToken,
      isMarkedAuthenticated,
      finalResult: isAuthenticated
    })
    
    if (isAuthenticated) {
      console.log('User authenticated - setting up standalone session')
      
      // Restore Firebase user to store if needed
      const currentUser = auth.getCurrentUser()
      if (currentUser && !authStore.user) {
        console.log('Restoring Firebase user to store')
        authStore.setUser(currentUser)
      }
      
      // Ensure we have user profile if authenticated through store
      if (authStore.isAuthenticated && !authStore.user_profile) {
        console.log('Fetching user profile...')
        try {
          await authStore.getUserProfile()
        } catch (error) {
          console.log('Failed to fetch user profile:', error)
          // Continue anyway, profile might be loaded later
        }
      }
      
      // Create standalone session if not already in standalone mode
      if (!authStore.isInStandaloneMode) {
        console.log('Creating standalone session')
        authStore.createStandaloneSession(authStore.user_profile)
      }
      
      // Store return URL if provided
      if (to.query.return_url) {
        sessionStorage.setItem('billing_return_url', to.query.return_url as string)
      }
      
      console.log('Proceeding to authenticated route')
      return next()
    }
    
    // If not authenticated, check for token in query params or session
    const token = to.query.token || sessionStorage.getItem('standalone_billing_token')
    console.log('Token available:', !!token)
    
    if (!token) {
      console.log('No token and not authenticated - redirecting to login')
      return next({
        name: 'standalone-billing-login',
        query: { redirect: to.fullPath }
      })
    }
    
    // Validate the token
    console.log('Validating token...')
    try {
      await authStore.validateStandaloneToken(token as string)
      
      // Store return URL if provided
      if (to.query.return_url) {
        sessionStorage.setItem('billing_return_url', to.query.return_url as string)
      }
      
      console.log('Token validated successfully')
      return next()
    } catch (error) {
      console.log('Token validation failed:', error)
      // Invalid token, redirect to login
      return next({
        name: 'standalone-billing-login',
        query: { error: 'invalid_token' }
      })
    }
  } else {
    // Route doesn't require standalone auth, proceed normally
    next()
  }
}